import type { ClientType } from "../../shared/hooks/use-client-type";
import type { useUserRoles } from "../../shared/hooks/use-user-roles";
import type { ApplicationStatus } from "../../constants";
import type { ApplicantDataFormValues } from "../create-application-page/create-application-page.lib";
import type { ApplicationDetailTab, ApplicationDetailTabKey } from "./application-detail-page.lib";

export type ApplicationDetailTabUserRoles = ReturnType<typeof useUserRoles>;

export type ApplicationPaymentTermsValue = "PENDING_REVIEW" | "NO_PAYMENT" | "PAID_SERVICE";

export const APPLICATION_PAYMENT_TERMS_OPTIONS: ReadonlyArray<{ value: ApplicationPaymentTermsValue; label: string }> = [
  { value: "PENDING_REVIEW", label: "На рассмотрении" },
  { value: "NO_PAYMENT", label: "Без оплаты" },
  { value: "PAID_SERVICE", label: "Платная услуга" },
] as const;

export const APPLICATION_DETAIL_TAB_DEFINITIONS: ApplicationDetailTab[] = [
  { key: "object-description", label: "Данные объекта" },
  { key: "documents", label: "Документы" },
  { key: "applicant", label: "О заявителе" },
  { key: "payer-details", label: "Реквизиты плательщика" },
  { key: "payment", label: "Оплата" },
  { key: "review-documents", label: "Документы рассмотрения" },
  { key: "comments", label: "Комментарии" },
  { key: "final-decision", label: "Результаты рассмотрений" },
  { key: "history", label: "История действий" },
];

const INTERNAL_STATUSES_BEFORE_NG_WORK: readonly ApplicationStatus[] = ["INTAKE_CHECK", "REVISION_REQUIRED", "SENT_TO_NG"];

const TERMINAL_DECISION_STATUSES: readonly ApplicationStatus[] = ["RESOLVED_APPROVED", "RESOLVED_REJECTED", "TU_ISSUED", "REJECTED"];

export type ApplicationDetailTabVisibilityContext = {
  clientType: ClientType;
  status?: ApplicationStatus;
  roles: ApplicationDetailTabUserRoles;
  applicantData: ApplicantDataFormValues | null;
  isPaidServiceRequired: boolean;
  hasFinalDecision: boolean;
};

export const isApplicationPaidServiceRequired = (params: {
  clientType: ClientType;
  status?: ApplicationStatus;
  paymentTerms?: ApplicationPaymentTermsValue | null;
  explicitPaidService?: boolean | null;
}): boolean => {
  if (params.explicitPaidService === true || params.paymentTerms === "PAID_SERVICE") {
    return true;
  }

  if (params.clientType === "external" && params.status === "WAITING_PAYMENT") {
    return true;
  }

  return false;
};

export const isApplicationDetailTabVisible = (
  tabKey: ApplicationDetailTabKey,
  { clientType, applicantData, isPaidServiceRequired }: ApplicationDetailTabVisibilityContext,
): boolean => {
  if (tabKey === "payer-details") {
    return applicantData?.paymentByAnotherOrganization === true;
  }

  if (tabKey === "payment") {
    if (clientType === "internal") {
      return true;
    }

    return clientType === "external" && isPaidServiceRequired;
  }

  if (tabKey === "review-documents") {
    return clientType === "internal";
  }

  return true;
};

export const isApplicationDetailTabDisabled = (
  tabKey: ApplicationDetailTabKey,
  { clientType, status }: ApplicationDetailTabVisibilityContext,
): boolean => {
  if (tabKey !== "payment" || clientType !== "internal" || !status) {
    return false;
  }

  return INTERNAL_STATUSES_BEFORE_NG_WORK.includes(status);
};

export const canEditApplicationPaymentTerms = ({ clientType, status, roles }: ApplicationDetailTabVisibilityContext): boolean =>
  clientType === "internal" && (status === "IN_NG" || status === "NG_APPROVAL") && roles.isInternalApparatusNg;

export const canUploadReviewDocuments = ({ clientType, status, roles }: ApplicationDetailTabVisibilityContext): boolean =>
  clientType === "internal" && status === "COMMISSION_PREP" && roles.isInternalNtpResponsible;

export const canDeleteReviewDocuments = ({ clientType, roles }: ApplicationDetailTabVisibilityContext): boolean =>
  clientType === "internal" && roles.isInternalNtpResponsible;

export const canUploadTuParts = ({ clientType, status, roles }: ApplicationDetailTabVisibilityContext): boolean =>
  clientType === "internal" && status === "DECISION_FORMING" && roles.isInternalNtpResponsible;

export const canDeleteTuParts = ({ clientType, roles }: ApplicationDetailTabVisibilityContext): boolean =>
  clientType === "internal" && roles.isInternalNtpResponsible;

export const canEditFinalDecision = ({ clientType, status, roles, hasFinalDecision }: ApplicationDetailTabVisibilityContext): boolean =>
  clientType === "internal" && (status === "DECISION_FORMING" || status === "DECISION_REWORK") && roles.isInternalNtpResponsible && hasFinalDecision;

/** Создание новой версии итогового решения (кнопка «Добавить версию»).
 * POST — если черновика нет (DECISION_FORMING или DECISION_REWORK после BAC-2324);
 * PUT — если черновик уже создан. */
export const canAddFinalDecisionVersion = ({ clientType, status, roles }: ApplicationDetailTabVisibilityContext): boolean =>
  clientType === "internal" && (status === "DECISION_FORMING" || status === "DECISION_REWORK") && roles.isInternalNtpResponsible;

/** Замечания к текущей версии итогового решения (до согласования или возврата на доработку). */
export const canManageFinalDecisionRemarks = ({ clientType, status, roles }: ApplicationDetailTabVisibilityContext): boolean => {
  if (clientType !== "internal") {
    return false;
  }

  if (status === "NTP_APPROVAL") {
    return roles.isInternalNtpChief;
  }

  if (status === "NG_APPROVAL") {
    return roles.isInternalApparatusNg;
  }

  return false;
};

export const shouldShowFinalDecisionPlaceholder = (params: { hasFinalDecision: boolean; status?: ApplicationStatus }): boolean => {
  if (params.hasFinalDecision) {
    return false;
  }

  if (params.status && TERMINAL_DECISION_STATUSES.includes(params.status)) {
    return false;
  }

  return true;
};

export const getVisibleApplicationDetailTabs = (context: ApplicationDetailTabVisibilityContext): ApplicationDetailTab[] =>
  APPLICATION_DETAIL_TAB_DEFINITIONS.filter((tab) => isApplicationDetailTabVisible(tab.key, context));

export const resolveApplicationDetailTabVisibilityContext = (params: {
  clientType: ClientType;
  status?: ApplicationStatus;
  roles: ApplicationDetailTabUserRoles;
  applicantData: ApplicantDataFormValues | null;
  paymentTerms?: ApplicationPaymentTermsValue | null;
  explicitPaidService?: boolean | null;
  hasFinalDecision: boolean;
}): ApplicationDetailTabVisibilityContext => ({
  clientType: params.clientType,
  status: params.status,
  roles: params.roles,
  applicantData: params.applicantData,
  isPaidServiceRequired: isApplicationPaidServiceRequired({
    clientType: params.clientType,
    status: params.status,
    paymentTerms: params.paymentTerms,
    explicitPaidService: params.explicitPaidService,
  }),
  hasFinalDecision: params.hasFinalDecision,
});

export const getDefaultApplicationDetailTabKey = (tabs: ApplicationDetailTab[]): ApplicationDetailTabKey => tabs[0]?.key ?? "object-description";

export const normalizeApplicationDetailActiveTab = (
  activeTab: ApplicationDetailTabKey,
  tabs: ApplicationDetailTab[],
  context: ApplicationDetailTabVisibilityContext,
): ApplicationDetailTabKey => {
  const visibleTabKeys = new Set(tabs.map((tab) => tab.key));

  if (visibleTabKeys.has(activeTab) && !isApplicationDetailTabDisabled(activeTab, context)) {
    return activeTab;
  }

  return getDefaultApplicationDetailTabKey(tabs);
};
